/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export interface TextInputProps extends FieldRenderProps<string, HTMLInputElement> {
  placeholder?: string;
  type: string;
  readOnly?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown?: (e: any) => void;
  selValue?: string;
  defValue?: string;
  unit?: string;
  maxLength?: number;
  validationError?: string;
}

export const TextInput: React.FC<TextInputProps> = ({ input, meta, ...rest }: TextInputProps): React.ReactElement => {
  const { type, placeholder, readOnly, onKeyDown, selValue, defValue, maxLength, validationError, disabled } = rest;
  const { touched, invalid, valid } = meta;

  return (
    <input
      {...input}
      id={input.name as string}
      type={type}
      readOnly={readOnly}
      disabled={disabled}
      placeholder={placeholder}
      value={input.value ? input.value : selValue || ''}
      defaultValue={defValue}
      onKeyDown={onKeyDown}
      className={`form-control ${(touched && invalid) || validationError ? 'is-invalid' : ''} ${
        touched && valid ? 'is-valid' : ''
      } ${disabled ? 'text-gray' : ''}`}
      autoComplete="current-password"
      maxLength={maxLength}
    />
  );
};

export interface TextInputFieldProps {
  id?: number;
  name: string;
  type: string;
  label?: string;
  floatingLabel?: React.ReactNode;
  placeholder?: string;
  readOnly?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown?: (e: any) => void;
  selValue?: string;
  defValue?: string;
  unit?: string;
  disabled?: boolean;
  className?: string;
  description?: string;
  maxLength?: number;
  validationError?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parse?: (value: any | null, name: string) => string;
}

export const TextInputField: React.FC<TextInputFieldProps> = (props: TextInputFieldProps): React.ReactElement => {
  const { id, name, label, floatingLabel, unit, className, description, selValue, validationError, disabled, parse } =
    props;

  return (
    <Field name={name} defaultValue={selValue} parse={parse}>
      {(fieldProps) => {
        return (
          <div className={`form-group ${className}`}>
            {label && (
              <div className="row">
                <div className="col">
                  <label
                    htmlFor={fieldProps.input.name}
                    className={`${validationError ? 'invalid-feedback-label' : ''} ${disabled ? 'text-gray' : ''}`}
                  >
                    {label}
                    {unit && unit !== '' && <span>({unit})</span>}
                    {description && description !== '' && (
                      <OverlayTrigger
                        key={id}
                        placement="right"
                        overlay={<Tooltip id={`tooltip-${id}`}>{description}</Tooltip>}
                      >
                        <i className="fa fa-question-circle" aria-hidden="true" />
                      </OverlayTrigger>
                    )}
                  </label>
                </div>
              </div>
            )}

            <div className={`${floatingLabel ? 'form-label-group floating-label' : ''}`}>
              <TextInput {...fieldProps} {...props} />
              {floatingLabel && <label htmlFor={fieldProps.input.name as string | undefined}>{floatingLabel}</label>}
              <div className="invalid-feedback" role="status">
                {fieldProps.meta.touched && fieldProps.meta.error && <span>{fieldProps.meta.error}</span>}
              </div>
              {validationError ? (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  <span>{validationError}</span>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        );
      }}
    </Field>
  );
};
